import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';
import { z } from 'zod';

import type { WeatherLocation } from './config';

// ─── Types ───────────────────────────────────────────────────────────────────

export type WmoIconKey =
  | 'clear-day'
  | 'clear-night'
  | 'partly-cloudy-day'
  | 'partly-cloudy-night'
  | 'cloudy'
  | 'fog'
  | 'drizzle'
  | 'rain'
  | 'snow'
  | 'thunderstorm'
  | 'sleet';

export interface WeatherCondition {
  code: number;
  label: string;
  iconKey: WmoIconKey;
}

export interface HourSlot {
  ts: number;
  tempC: number;
  feelsLikeC: number;
  humidity: number;
  precipMm: number;
  precipProb: number;
  windMps: number;
  windDirDeg: number;
  condition: WeatherCondition;
}

export interface DaySummary {
  dateMs: number;
  weekdayShort: string;
  tempMinC: number;
  tempMaxC: number;
  precipSumMm: number;
  precipChanceMax: number;
  windMaxMps: number;
  condition: WeatherCondition;
  sunriseMs: number;
  sunsetMs: number;
  daylightSec: number;
}

export interface SunEvent {
  type: 'sunrise' | 'sunset';
  ms: number;
}

export interface WeatherSnapshot {
  city: string;
  now: {
    tempC: number;
    feelsLikeC: number;
    humidity: number;
    windMps: number;
    /** Meteorological direction the wind blows FROM (0 = N); null when unavailable. */
    windDirDeg: number | null;
    condition: WeatherCondition;
  } | null;
  todayRemainingStrip: HourSlot[];
  today: { hourly: HourSlot[]; summary: DaySummary } | null;
  tomorrow: { hourly: HourSlot[]; summary: DaySummary } | null;
  week: DaySummary[];
  nextSunEvent: SunEvent | null;
}

// ─── WMO → condition mapping ─────────────────────────────────────────────────

interface ConditionEntry {
  label: string;
  /** Same icon for day/night when null in day/night-specific maps. */
  dayIcon: WmoIconKey;
  nightIcon: WmoIconKey;
}

/**
 * WMO weather codes → label + day/night icon variants.
 * See https://open-meteo.com/en/docs (WMO Weather interpretation codes).
 */
const WMO_TO_CONDITION: Record<number, ConditionEntry> = {
  0: { label: 'Clear sky', dayIcon: 'clear-day', nightIcon: 'clear-night' },
  1: { label: 'Mainly clear', dayIcon: 'clear-day', nightIcon: 'clear-night' },
  2: { label: 'Partly cloudy', dayIcon: 'partly-cloudy-day', nightIcon: 'partly-cloudy-night' },
  3: { label: 'Overcast', dayIcon: 'cloudy', nightIcon: 'cloudy' },
  45: { label: 'Fog', dayIcon: 'fog', nightIcon: 'fog' },
  48: { label: 'Rime fog', dayIcon: 'fog', nightIcon: 'fog' },
  51: { label: 'Light drizzle', dayIcon: 'drizzle', nightIcon: 'drizzle' },
  53: { label: 'Drizzle', dayIcon: 'drizzle', nightIcon: 'drizzle' },
  55: { label: 'Heavy drizzle', dayIcon: 'drizzle', nightIcon: 'drizzle' },
  56: { label: 'Freezing drizzle', dayIcon: 'sleet', nightIcon: 'sleet' },
  57: { label: 'Freezing drizzle', dayIcon: 'sleet', nightIcon: 'sleet' },
  61: { label: 'Light rain', dayIcon: 'rain', nightIcon: 'rain' },
  63: { label: 'Rain', dayIcon: 'rain', nightIcon: 'rain' },
  65: { label: 'Heavy rain', dayIcon: 'rain', nightIcon: 'rain' },
  66: { label: 'Freezing rain', dayIcon: 'sleet', nightIcon: 'sleet' },
  67: { label: 'Freezing rain', dayIcon: 'sleet', nightIcon: 'sleet' },
  71: { label: 'Light snow', dayIcon: 'snow', nightIcon: 'snow' },
  73: { label: 'Snow', dayIcon: 'snow', nightIcon: 'snow' },
  75: { label: 'Heavy snow', dayIcon: 'snow', nightIcon: 'snow' },
  77: { label: 'Snow grains', dayIcon: 'snow', nightIcon: 'snow' },
  80: { label: 'Rain showers', dayIcon: 'rain', nightIcon: 'rain' },
  81: { label: 'Rain showers', dayIcon: 'rain', nightIcon: 'rain' },
  82: { label: 'Violent showers', dayIcon: 'rain', nightIcon: 'rain' },
  85: { label: 'Snow showers', dayIcon: 'snow', nightIcon: 'snow' },
  86: { label: 'Heavy snow showers', dayIcon: 'snow', nightIcon: 'snow' },
  95: { label: 'Thunderstorm', dayIcon: 'thunderstorm', nightIcon: 'thunderstorm' },
  96: { label: 'Thunderstorm w/ hail', dayIcon: 'thunderstorm', nightIcon: 'thunderstorm' },
  99: { label: 'Thunderstorm w/ hail', dayIcon: 'thunderstorm', nightIcon: 'thunderstorm' },
};

export function conditionFromCode(code: number, isDay: boolean): WeatherCondition {
  const entry = WMO_TO_CONDITION[code];
  if (!entry) return { code, label: 'Unknown', iconKey: 'cloudy' };
  return { code, label: entry.label, iconKey: isDay ? entry.dayIcon : entry.nightIcon };
}

// ─── Open-Meteo response schema ──────────────────────────────────────────────

const OpenMeteoSchema = z.object({
  current: z
    .object({
      time: z.string(),
      temperature_2m: z.number(),
      relative_humidity_2m: z.number(),
      apparent_temperature: z.number(),
      weather_code: z.number(),
      wind_speed_10m: z.number(),
      wind_direction_10m: z.number().optional(),
    })
    .optional(),
  hourly: z.object({
    time: z.array(z.string()),
    temperature_2m: z.array(z.number()),
    apparent_temperature: z.array(z.number()).optional(),
    relative_humidity_2m: z.array(z.number()),
    precipitation: z.array(z.number()),
    precipitation_probability: z.array(z.number().nullable()),
    weather_code: z.array(z.number()),
    wind_speed_10m: z.array(z.number()),
    wind_direction_10m: z.array(z.number()),
  }),
  daily: z.object({
    time: z.array(z.string()),
    weather_code: z.array(z.number()),
    temperature_2m_max: z.array(z.number()),
    temperature_2m_min: z.array(z.number()),
    precipitation_sum: z.array(z.number()),
    precipitation_probability_max: z.array(z.number().nullable()),
    wind_speed_10m_max: z.array(z.number()),
    sunrise: z.array(z.string()),
    sunset: z.array(z.string()),
    daylight_duration: z.array(z.number()),
  }),
});

export type OpenMeteoRaw = z.infer<typeof OpenMeteoSchema>;

// ─── Parse helpers ───────────────────────────────────────────────────────────

/** Parse a date-time string that Open-Meteo returns in the requested timezone (no offset). */
function parseZonedToMs(zoned: string, tz: string): number {
  return fromZonedTime(zoned, tz).getTime();
}

/** Local YYYY-MM-DD for a given UTC ms, in the requested tz. */
export function localDayKey(ms: number, tz: string): string {
  return formatInTimeZone(ms, tz, 'yyyy-MM-dd');
}

/** Weekday short label in the requested tz (e.g. "Mon"). */
function weekdayShort(ms: number, tz: string): string {
  return formatInTimeZone(ms, tz, 'EEE');
}

/** Find the daily entry for the local day that contains `ms`. */
function findDayForMs(daily: DaySummary[], ms: number, tz: string): DaySummary | null {
  const key = localDayKey(ms, tz);
  return daily.find((d) => localDayKey(d.dateMs, tz) === key) ?? null;
}

/** True if `nowMs` falls between today's sunrise and sunset (used to pick day vs night icon). */
function isDayAt(nowMs: number, today: DaySummary | null): boolean {
  if (!today) return true;
  return nowMs >= today.sunriseMs && nowMs < today.sunsetMs;
}

// ─── splitHourlyByLocalDay ───────────────────────────────────────────────────

export function splitHourlyByLocalDay(slots: HourSlot[], nowMs: number, tz: string): { today: HourSlot[]; tomorrow: HourSlot[] } {
  const todayKey = localDayKey(nowMs, tz);
  // Find any slot on tomorrow by looking ahead 24h+ from now and reading its day key.
  const tomorrowKey = localDayKey(nowMs + 24 * 60 * 60 * 1000, tz);

  const today = slots.filter((s) => localDayKey(s.ts, tz) === todayKey);
  const tomorrow = slots.filter((s) => localDayKey(s.ts, tz) === tomorrowKey);
  return { today, tomorrow };
}

// ─── pickRestOfDayStrip ──────────────────────────────────────────────────────

/**
 * Pick up to `max` evenly-distributed slots from `todayHourly` starting at or after `now`.
 * Always includes the first remaining slot and the last remaining slot when sampling.
 */
export function pickRestOfDayStrip(todayHourly: HourSlot[], nowMs: number, max: number): HourSlot[] {
  const remaining = todayHourly.filter((s) => s.ts >= alignToHour(nowMs));
  if (remaining.length === 0) return [];
  if (remaining.length <= max) return remaining;

  // Even sample including first and last.
  const result: HourSlot[] = [];
  const lastIdx = remaining.length - 1;
  for (let i = 0; i < max; i++) {
    const idx = Math.round((i * lastIdx) / (max - 1));
    result.push(remaining[idx]!);
  }
  return result;
}

function alignToHour(ms: number): number {
  const d = new Date(ms);
  d.setMinutes(0, 0, 0);
  return d.getTime();
}

// ─── nextSunEvent ────────────────────────────────────────────────────────────

export function nextSunEvent(nowMs: number, todayDay: DaySummary | null, tomorrowDay: DaySummary | null): SunEvent | null {
  if (todayDay) {
    if (nowMs < todayDay.sunriseMs) return { type: 'sunrise', ms: todayDay.sunriseMs };
    if (nowMs < todayDay.sunsetMs) return { type: 'sunset', ms: todayDay.sunsetMs };
  }
  if (tomorrowDay) return { type: 'sunrise', ms: tomorrowDay.sunriseMs };
  return null;
}

// ─── Formatters ──────────────────────────────────────────────────────────────

/** "2 h 13 m", "45 m", "now". Negative values clamp to "now". */
export function formatTimeUntil(targetMs: number, nowMs: number): string {
  const diff = Math.max(0, targetMs - nowMs);
  const totalMin = Math.floor(diff / 60_000);
  if (totalMin < 1) return 'now';
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m} m`;
  return `${h} h ${m} m`;
}

/** "9 h 12 m" from a number of seconds. */
export function formatDaylight(seconds: number): string {
  const totalMin = Math.floor(seconds / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h} h ${m} m`;
}

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

/** 8-point compass label for a direction in degrees (0 = N). */
export function compassFromDeg(deg: number): string {
  return COMPASS[Math.round((((deg % 360) + 360) % 360) / 45) % 8]!;
}

// ─── parseOpenMeteoResponse ──────────────────────────────────────────────────

/**
 * Validate the raw Open-Meteo JSON and build a `WeatherSnapshot`.
 * Throws if the response shape doesn't match.
 */
export function parseOpenMeteoResponse(raw: unknown, now: Date, location: WeatherLocation): WeatherSnapshot {
  const parsed = OpenMeteoSchema.parse(raw);
  const nowMs = now.getTime();
  const tz = location.tz;

  // Daily summaries first — we need them to resolve isDay for the current snapshot.
  const week: DaySummary[] = parsed.daily.time.map((dateStr, i) => {
    const sunriseMs = parseZonedToMs(parsed.daily.sunrise[i]!, tz);
    const sunsetMs = parseZonedToMs(parsed.daily.sunset[i]!, tz);
    const dateMs = parseZonedToMs(`${dateStr}T00:00:00`, tz);
    const isDay = nowMs >= sunriseMs && nowMs < sunsetMs;
    return {
      dateMs,
      weekdayShort: weekdayShort(dateMs, tz),
      tempMinC: parsed.daily.temperature_2m_min[i]!,
      tempMaxC: parsed.daily.temperature_2m_max[i]!,
      precipSumMm: parsed.daily.precipitation_sum[i]!,
      precipChanceMax: parsed.daily.precipitation_probability_max[i] ?? 0,
      windMaxMps: parsed.daily.wind_speed_10m_max[i]!,
      condition: conditionFromCode(parsed.daily.weather_code[i]!, isDay),
      sunriseMs,
      sunsetMs,
      daylightSec: parsed.daily.daylight_duration[i]!,
    };
  });

  const todayDay = findDayForMs(week, nowMs, tz);
  const tomorrowDay = findDayForMs(week, nowMs + 24 * 60 * 60 * 1000, tz);

  // Hourly slots.
  const hourly: HourSlot[] = parsed.hourly.time.map((t, i) => {
    const ts = parseZonedToMs(t, tz);
    const isDay = isDayAt(ts, findDayForMs(week, ts, tz));
    return {
      ts,
      tempC: parsed.hourly.temperature_2m[i]!,
      feelsLikeC: parsed.hourly.apparent_temperature?.[i] ?? parsed.hourly.temperature_2m[i]!,
      humidity: parsed.hourly.relative_humidity_2m[i]!,
      precipMm: parsed.hourly.precipitation[i]!,
      precipProb: parsed.hourly.precipitation_probability[i] ?? 0,
      windMps: parsed.hourly.wind_speed_10m[i]!,
      windDirDeg: parsed.hourly.wind_direction_10m[i]!,
      condition: conditionFromCode(parsed.hourly.weather_code[i]!, isDay),
    };
  });

  const { today: todayHourly, tomorrow: tomorrowHourly } = splitHourlyByLocalDay(hourly, nowMs, tz);

  // Current snapshot.
  let nowSnap: WeatherSnapshot['now'] = null;
  if (parsed.current) {
    const isDay = isDayAt(nowMs, todayDay);
    nowSnap = {
      tempC: parsed.current.temperature_2m,
      feelsLikeC: parsed.current.apparent_temperature,
      humidity: parsed.current.relative_humidity_2m,
      windMps: parsed.current.wind_speed_10m,
      // Older function deployments don't request current direction; fall back to this hour's forecast.
      windDirDeg: parsed.current.wind_direction_10m ?? hourly.find((h) => h.ts === alignToHour(nowMs))?.windDirDeg ?? null,
      condition: conditionFromCode(parsed.current.weather_code, isDay),
    };
  }

  const todayRemainingStrip = pickRestOfDayStrip(todayHourly, nowMs, 6);

  return {
    city: location.city,
    now: nowSnap,
    todayRemainingStrip,
    today: todayDay ? { hourly: todayHourly, summary: todayDay } : null,
    tomorrow: tomorrowDay ? { hourly: tomorrowHourly, summary: tomorrowDay } : null,
    week,
    nextSunEvent: nextSunEvent(nowMs, todayDay, tomorrowDay),
  };
}

// Re-export tz helper for tests if needed.
export { toZonedTime };
